import React, { useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components'
import emailjs from '@emailjs/browser';
import { Snackbar, Alert } from '@mui/material';

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 60px 20px;
  
  @media (max-width: 960px) {
    padding: 40px 16px;
  }
`

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1350px;
  padding: 0px 0px 60px 0px;
  gap: 12px;
`

const Title = styled.h2`
  font-size: 48px;
  text-align: center;
  font-weight: 700;
  margin-top: 20px;
  background: ${({ theme }) => theme.gradient_text};
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 3s linear infinite;
  
  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 36px;
  }
`;

const Desc = styled.p`
  font-size: 18px;
  text-align: center;
  max-width: 600px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.6;
  
  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 16px;
  }
`;

const ContactForm = styled.form`
  width: 95%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.glass};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  padding: 36px;
  border-radius: 24px;
  border: 1px solid ${({ theme }) => theme.glass_border};
  box-shadow: 0 25px 50px ${({ theme }) => theme.shadow_primary};
  margin-top: 28px;
  gap: 16px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme }) => theme.gradient_primary};
    animation: ${pulse} 3s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, ${({ theme }) => theme.primary + '05'} 0%, transparent 70%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 24px;
  }
`

const ContactTitle = styled.h3`
  font-size: 26px;
  margin-bottom: 8px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  display: flex;
  align-items: center;
  gap: 12px;
`

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const InputLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text_secondary};
  font-family: 'JetBrains Mono', monospace;
`

const ContactInput = styled.input`
  flex: 1;
  background-color: ${({ theme }) => theme.bg};
  border: 2px solid ${({ theme }) => theme.glass_border};
  outline: none;
  font-size: 16px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  padding: 14px 18px;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 20px ${({ theme }) => theme.shadow_primary};
    background: ${({ theme }) => theme.card};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.text_secondary + '80'};
  }
`

const ContactInputMessage = styled.textarea`
  flex: 1;
  background-color: ${({ theme }) => theme.bg};
  border: 2px solid ${({ theme }) => theme.glass_border};
  outline: none;
  font-size: 16px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  padding: 14px 18px;
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 140px;
  font-family: inherit;
  
  &:focus {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 20px ${({ theme }) => theme.shadow_primary};
    background: ${({ theme }) => theme.card};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.text_secondary + '80'};
  }
`

const ContactButton = styled.button`
  width: 100%;
  text-decoration: none;
  text-align: center;
  background: ${({ theme }) => theme.gradient_primary};
  padding: 16px 24px;
  margin-top: 12px;
  border-radius: 12px;
  border: none;
  color: ${({ theme }) => theme.bg};
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.4s ease;
  box-shadow: 0 10px 30px ${({ theme }) => theme.shadow_primary};
  position: relative;
  overflow: hidden;
  letter-spacing: 1px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px ${({ theme }) => theme.shadow_primary}, 
                0 0 40px ${({ theme }) => theme.shadow_accent};
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`

const Contact = () => {
  const form = useRef();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState({
    type: 'success',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    emailjs.sendForm(
      'service_y5p3fzs',
      'template_1e3uk3d',
      form.current,
      'ommidSqET1FGWZSyf'
    )
      .then((result) => {
        setStatus({
          type: 'success',
          message: 'Message sent successfully! I\'ll get back to you soon.'
        });
        form.current.reset();
        setLoading(false);
      })
      .catch((error) => {
        setStatus({
          type: 'error',
          message: 'Failed to send message. Please try again.'
        });
        setLoading(false);
      })
      .finally(() => {
        setOpen(true);
      });
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <Container id="contact">
      <Wrapper>
        <Title>Get In Touch</Title>
        <Desc>
          I'm always open to discussing new projects, creative ideas, or opportunities to be part of your visions.
        </Desc>
        <ContactForm ref={form} onSubmit={handleSubmit}>
          <ContactTitle>
            Let's Connect 🚀
          </ContactTitle>
          <InputGroup>
            <InputLabel>{'// your_email'}</InputLabel>
            <ContactInput
              placeholder="hello@example.com"
              name="user_email"
              type="email"
              required
            />
          </InputGroup>
          <InputGroup>
            <InputLabel>{'// your_name'}</InputLabel>
            <ContactInput
              placeholder="John Doe"
              name="user_name"
              type="text"
              required
            />
          </InputGroup>
          <InputGroup>
            <InputLabel>{'// subject'}</InputLabel>
            <ContactInput
              placeholder="Project Collaboration"
              name="subject"
              type="text"
              required
            />
          </InputGroup>
          <InputGroup>
            <InputLabel>{'// message'}</InputLabel>
            <ContactInputMessage
              placeholder="Tell me about your project..."
              rows="4"
              name="message"
              required
            />
          </InputGroup>
          <ContactButton
            type="submit"
            disabled={loading}
          >
            {loading ? "Sending..." : "Send Message"}
          </ContactButton>
        </ContactForm>
        <Snackbar
          open={open}
          autoHideDuration={6000}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleClose}
            severity={status.type}
            sx={{
              width: '100%',
              background: 'rgba(13, 18, 36, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(0, 255, 255, 0.2)',
              color: '#f0f0f0'
            }}
          >
            {status.message}
          </Alert>
        </Snackbar>
      </Wrapper>
    </Container>
  );
};

export default Contact